import React, { FunctionComponent, ReactElement, useMemo } from "react";

import { useBoolean } from "../utils/componentUtils";

export interface WaitIndicatorProps {
    title: string;
    description: string;
}

export const WaitIndicator: FunctionComponent<WaitIndicatorProps> = ({ title, description }) => {
    // NOTE: Styles for the classes used by this component are defined in commons-ui-styles
    return (
        <React.Fragment>
            <div className="ipswitch_waitindicator_mask" />
            <div className="ipswitch_waitindicator">
                <div className="waitindicator_content">
                    <div className="waitindicator_spinner">
                        <div className="ipswitch_waitspinner" />
                    </div>
                    <div className="waitindicator_text">
                        <div className="waitindicator_title">{title}</div>
                        <div className="waitindicator_description">{description}</div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

/**
 * Creates a wait element with methods to enable and disable it.
 */
export const useWaitIndicator = (
    title: string,
    description: string
): [ReactElement, () => void, () => void] => {
    const [flag, setTrue, setFalse] = useBoolean(false);
    const waitElement = useMemo(
        () => <WaitIndicator title={title} description={description} />,
        []
    );

    return [flag ? waitElement : null, setTrue, setFalse];
};

/**
 * Creates a wait element with a method to display it while a provided
 * promise is active, and hide it when the promise settles.
 */
export const useWaitOnPromise = (
    title: string,
    description: string
): [ReactElement, (promise: Promise<any>) => void] => {
    const [waitElement, showWait, hideWait] = useWaitIndicator(title, description);

    const waitFunction = (promise: Promise<any>): void => {
        showWait();
        promise.finally(hideWait);
    };

    return [waitElement, waitFunction];
};
